import React, { useEffect, useState } from "react";
import "./Toast.css";

/**
 * Global toast renderer. Mounted once at the app root; listens for
 * `chariot:toast` window events dispatched by showToast(). Renders a
 * stack of pill-shaped notifications in the top-right that self-dismiss.
 *
 * Stays decoupled from React context so any code (fetch interceptors,
 * thrown errors in a hook, plain util functions) can fire a toast.
 */
export default function Toast() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    // Track active dismiss timers so we can clear them on unmount —
    // otherwise an unmounted-but-pending timeout will call setToasts on
    // a dead component and React logs a warning. Toast is mounted at App
    // root so this rarely fires in practice, but the cleanup is cheap.
    const timers = new Set();
    const handler = (event) => {
      const t = event.detail;
      if (!t || !t.id) return;
      setToasts((prev) => [...prev, t]);
      const ms = typeof t.durationMs === "number" ? t.durationMs : 4000;
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        setToasts((prev) => prev.filter((x) => x.id !== t.id));
      }, ms);
      timers.add(timer);
    };
    window.addEventListener("chariot:toast", handler);
    return () => {
      window.removeEventListener("chariot:toast", handler);
      timers.forEach((id) => window.clearTimeout(id));
      timers.clear();
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="chariot-toast-stack" aria-live="polite" aria-atomic="false">
      {toasts.map((t) => (
        <div key={t.id} className={`chariot-toast chariot-toast-${t.kind || "info"}`} role="status">
          <div className="chariot-toast-icon" aria-hidden="true">
            {iconFor(t.kind)}
          </div>
          <div className="chariot-toast-body">
            {t.title && <div className="chariot-toast-title">{t.title}</div>}
            <div className="chariot-toast-message">{t.message}</div>
          </div>
          <button
            type="button"
            className="chariot-toast-dismiss"
            aria-label="Dismiss"
            onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

function iconFor(kind) {
  switch (kind) {
    case "success":
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    case "error":
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      );
    case "warning":
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case "info":
    default:
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      );
  }
}
